import { Box, Stack, Typography } from '@mui/material'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import StyledTimeline from './order-tracking.styles'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { useGetOrderStatusQuery } from 'store/orders/orders.api'
import { skipToken } from '@reduxjs/toolkit/query'
import { useMemo } from 'react'
import OrderTrackingTask from './order-tracking-task/order-tracking-task.component'
import { OrderStatus } from 'store/orders/orders.types'
import { useUpdateTripTaskStatusMutation } from 'store/trips/trips.api'

const Heading = () => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{ px: 2.5, pt: 2.5, pb: 1.5, flexShrink: 0 }}
  >
    <Typography fontWeight={700} sx={{ color: 'secondary.dark' }}>
      Tracking
    </Typography>
    <MapOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
  </Stack>
)

const OrderTracking = () => {
  const { selectedOrder } = useSelector(
    (state: RootState) => state.orders
  )

  const { data: orderStatusData, refetch } = useGetOrderStatusQuery(
    selectedOrder?.orderId && !selectedOrder.isTempLcDoc
      ? { orderId: selectedOrder.orderId }
      : skipToken
  )

  const [updateTripTaskStatus] = useUpdateTripTaskStatusMutation()

  const isPendingLcDoc = selectedOrder?.isTempLcDoc
  const fileName = selectedOrder?.fileName || selectedOrder?.customerName || ''
  const isFailedFile = fileName.startsWith('FAILED_')

  // Process tasks: only Pickup/Delivery (skip hook/drop trailer); Pickup first, then Delivery
  const processedTasks = useMemo(() => {
    if (!orderStatusData || orderStatusData.length === 0) return []

    const pickupAndDeliveryTasks = orderStatusData.filter(
      (task) => task.type === 'PICKUP' || task.type === 'DELIVERY'
    )

    // Sort so Pickup tasks always come before Delivery tasks (stable order within same type)
    const sortedTasks = [...pickupAndDeliveryTasks].sort((a, b) => {
      if (a.type === b.type) return 0
      return a.type === 'PICKUP' ? -1 : 1
    })

    return sortedTasks.map((task, index) => {
      // Only show "Pickup" or "Delivery" once as a section heading (first of each type in sorted list)
      let heading = ''
      if (task.type === 'PICKUP') {
        const isFirstPickup = !sortedTasks
          .slice(0, index)
          .some((t) => t.type === 'PICKUP')
        heading = isFirstPickup ? 'Pickup' : ''
      } else {
        const isFirstDelivery = !sortedTasks
          .slice(0, index)
          .some((t) => t.type === 'DELIVERY')
        heading = isFirstDelivery ? 'Delivery' : ''
      }
      return { task, heading }
    })
  }, [orderStatusData])

  const handleMarkComplete = async (task: OrderStatus) => {
    if (!task.tripId || !task.tripTaskId) {
      console.error('Missing tripId or tripTaskId')
      return
    }

    try {
      await updateTripTaskStatus({
        tripId: task.tripId,
        taskId: task.tripTaskId,
        status: 'COMPLETED',
      }).unwrap()
      // Refetch the order status to update the UI
      refetch()
    } catch (error) {
      console.error('Failed to update trip task status:', error)
    }
  }

  if (isPendingLcDoc) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          flex: 1,
          alignSelf: 'stretch',
          height: '100%',
        }}
      >
        <Heading />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
          }}
        >
          <Typography fontWeight={700} sx={{ color: isFailedFile ? 'error.main' : 'success.main' }}>
            {isFailedFile ? 'Failed' : 'Processing'}
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flex: 1,
        alignSelf: 'stretch',
        height: '100%',
      }}
    >
      <Heading />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehaviorY: 'contain',
          overscrollBehaviorX: 'none',
          WebkitOverflowScrolling: 'touch',
          scrollbarGutter: 'stable',
        }}
      >
        <StyledTimeline>
          {processedTasks.map((item, index) => (
            <OrderTrackingTask
              key={`${item.task.name}_${item.task.type}_${index}`}
              task={item.task}
              heading={item.heading}
              isLast={index === processedTasks.length - 1}
              onMarkComplete={handleMarkComplete}
            />
          ))}
        </StyledTimeline>
      </Box>
    </Box>
  )
}

export default OrderTracking
